import { useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { MenuIcon } from "lucide-react";

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/shared/ui/drawer";

import { BalanceGroup, UserPermissions, type User } from "@/entities/user";
import { CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";

import { PauseButton } from "@/features/PauseButton";

import { ProfileModal } from "../ProfileModal";
import { LogoutModal } from "../LogoutModal";
import { getNavRoutes } from "../SideBar/model/nav-routes";
import { MentorInfo } from "../MentorInfoModal";
import { ChangeContactsModal } from "../ChangeContactsModal";
import { SwitchThemeButton } from "@/features/SwitchThemeButton";
import { SendNotificationBanner } from "@/widgets/SendNotificationBanner";
import { SendNotificationCard } from "@/widgets/SendNotificationCard/ui/SendNotificationCard.tsx";
import { BroadcastToLeadsModal } from "@/widgets/BroadcastToLeadsModal";
// import SMSModal from "../SMSModal/SMSModal";
import { Roles } from "@/entities/user";
import { MasterTeamleadModal } from "../MasterTeamleadModal";
import { TelegramAssignButton } from "@/features/TelegramAssignButton";
import { PhonesExportModal } from "../PhoneExportModal";
import { MentorProfileModal } from "@/features/MentorProfileModal/MentorProfileModal";
import { ConvertBalancesCard } from "../ConvertBalancesCard";
import { CollaborateModal } from "../CollaborateModal";

export const MobileDrawer = ({ user }: { user: User | null }) => {
  const navigate = useNavigate();

  const location = useLocation();
  const [open, setOpen] = useState(false);
  const role = user?.role;
  const permissions = user?.permissions;

  const handleNavigate = (link: string) => {
    navigate(link);
    setOpen(false);
  };

  if (!user) return null;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="secondary" className="flex-grow">
          <MenuIcon />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[80svh] min-h-[80svh] border-t p-4">
        <DrawerTitle />
        <div className="w-full space-y-2 overflow-y-scroll">
          <div className="flex justify-between">
            <ProfileModal user={user} />
            {role === Roles.MasterTeamLead ||
            localStorage.getItem("master-teamlead-store") ? (
              <MasterTeamleadModal />
            ) : (
              <LogoutModal />
            )}
          </div>
          {[
            Roles.Worker,
            Roles.Moderator,
            Roles.Mentor,
            Roles.MasterMentor,
            Roles.MasterModerator,
            Roles.Head,
            Roles.TrafficManager,
            Roles.MasterTrafficManager,
          ].includes(role!) && (
            <div className="space-y-2">
              <BalanceGroup />
              {(user?.balances?.find((b) => b.asset === "FT_COIN")?.balance ||
                0) > 0 && <ConvertBalancesCard />}
              <CollaborateModal />
            </div>
          )}

          <div className="py-6 empty:hidden">
            <TelegramAssignButton />
            {role === Roles.Worker && <MentorInfo />}
          </div>

          <CardTitle className="text-sm font-normal tracking-normal text-textColor">
            Навигация
          </CardTitle>
          <nav className="w-full">
            <div className="grid grid-cols-2 gap-2">
              {getNavRoutes(role || "").map(({ label, link }) => {
                if (
                  link === "/bots" &&
                  role !== Roles.Head &&
                  !permissions?.includes(UserPermissions.CONSTRUCTOR_BOT)
                )
                  return;

                return (
                  <li
                    key={label}
                    className={`grid ${location.pathname === link ? "bg-black text-white" : ""} w-full place-items-center gap-2 rounded-lg p-2 text-textColor shadow hover:cursor-pointer`}
                    onClick={() => handleNavigate(link)}
                  >
                    {label}
                  </li>
                );
              })}
            </div>
          </nav>
          <div className="flex w-full flex-col gap-2 self-start">
            {[Roles.Mentor, Roles.MasterMentor].includes(role!) && (
              <>
                <ChangeContactsModal />
                <PauseButton />
              </>
            )}
            {role === Roles.Head && <SendNotificationBanner />}
            {role === Roles.Head && <SendNotificationCard />}
            {role === Roles.Head && <PhonesExportModal />}
            {[Roles.Mentor, Roles.MasterMentor].includes(role!) && (
              <MentorProfileModal />
            )}
            {/* {(role === Roles.Head || permissions?.includes("sms")) && (
              <SMSModal />
            )} */}
            {role === Roles.Head && <BroadcastToLeadsModal />}
          </div>
          <CardTitle className="text-sm font-normal tracking-normal text-textColor">
            Тема
          </CardTitle>
          <div>
            <SwitchThemeButton />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
